import { Box, Text } from '@fozito/common-ui';
import authService from 'modules/auth/services';
import {
  EPaymentType,
  TicketDetail,
  TicketPayment,
} from 'modules/tickets/services/types';
import React from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import { formatPhone } from 'utils/formatPhone';
import storage from 'utils/sessionStorage';
import fozito_logo from 'assets/logo.svg';
import fozito_logo_printer from 'assets/logo-printer.svg';
import clsx from 'clsx';
import { ICouponPrintDiscount } from 'modules/shop/types/coupon';
import CouponDiscount from 'modules/POS/Receipts/coupon/Discount';

// Styled Components
const ReceiptContainer = styled.div`
  display: flex;
  justify-content: center;
  &.printer {
    .color_text_1 {
      color: white !important;
    }
    .color_text_5 {
      color: black !important;
    }

    .title_h1_36_semibold {
      font-size: 24px;
    }

    .title_h7_20_semibold_caplock,
    .title_h5_24_semibold {
      font-size: 16px;
    }
    .custom-text,
    .title_h7_20_semibold_caplock,
    .content_content_2_16_medium {
      font-size: 12px;
    }

    .title_h9_18_semibold {
      line-height: 18px;
    }
  }
`;

const ReceiptWrapper = styled.div`
  width: 100%;
  max-width: 400px;
  background-color: white;
  padding: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  text-align: left;
  &.printer {
    max-width: unset;
    box-shadow: unset;
    padding: 0;
    padding-right: 1px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  text-align: right;
`;

const StoreInfo = styled.div`
  &.printer {
    .text-business-name {
      font-size: 16px;
    }
  }
`;

const ReceiptNumber = styled(Text)`
  height: 50px;
  background-color: black;
  gap: 10px;
  padding: 0px 8px;
  border-radius: 5px;
  &.printer {
    height: unset;
    padding: 4px 8px;
  }
`;

const Divider = styled.div`
  border: 0;
  border-top: 2px solid black;
`;

const CustomerInfo = styled.div``;

const CustomerHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

const CustomTag = styled(Text)`
  background-color: black;
  padding: 2px 4px;
  display: inline-block;
`;
const StaffNameTag = styled(CustomTag)`
  margin-top: 8px;
  width: 100%;
  margin-bottom: 10px;
  &.printer {
    margin-top: 0;
    font-size: 16px;
  }
`;

const ItemList = styled.div``;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ItemLabel = styled.div`
  display: flex;
  width: 100%;
  flex: 1;
`;

const ItemPrice = styled(ItemLabel)`
  justify-content: flex-end;
  align-items: center;
  gap: 5px;
`;

const FeeRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  border-top: 1px solid black;
  font-weight: bold;
`;

const PaymentSection = styled.div`
  padding-top: 8px;
  margin-bottom: 16px;
`;

const PaymentRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-weight: 500;
  margin-bottom: 5px;
`;

const SignatureSection = styled.div`
  margin-bottom: 8px;
`;

const SignatureContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: 16px;
  border-bottom: 1px solid black;
  margin-top: -32px;
`;

const SignatureImage = styled.img`
  width: 100%;
  height: auto;
  margin-bottom: -24px;
`;

const SignatureText = styled(Text)`
  text-align: center;
  margin-top: 8px;
`;

const Footer = styled.div`
  margin-top: 18px;
  text-align: center;
`;

const PosInfo = styled(Text)`
  background-color: black;
  padding: 4px 8px;
  margin: 8px 0px;
  display: inline-block;
`;

const getPaymentMethodDisplay = (paymentType: string) => {
  switch (paymentType) {
    case EPaymentType.CREDIT_CARD:
      return 'Credit Card';
    case EPaymentType.CASH:
      return 'Cash';
    case EPaymentType.CHECK:
      return 'Check';
    case EPaymentType.GIFT_CARD:
      return 'Gift Card';
    case EPaymentType.LOYALTY_POINT:
      return 'Loyalty Points';
    case EPaymentType.CASH_APPS:
      return 'Cash Apps';
    case EPaymentType.PAYPAL:
      return 'Paypal';
    case EPaymentType.VENMO:
      return 'Venmo';
    case EPaymentType.ZELLE:
      return 'Zelle';
    case EPaymentType.EBT_FOOD:
      return 'EBT FOOD';
    case EPaymentType.EBT_CASH:
      return 'EBT CASH';
    default:
      return 'Other Payment';
  }
};
// Format date from ISO string
const formatDate = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return (
    date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    }) +
    ' ' +
    date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
  );
};
type Props = {
  billRef?: React.LegacyRef<HTMLDivElement> | undefined;
  ticketDetail?: TicketDetail | null;
  coupons?: ICouponPrintDiscount[];
  printer?: boolean;
  owner?: boolean;
};
const ReceiptTicketUI = ({
  owner,
  billRef,
  ticketDetail,
  printer,
  coupons = [],
}: Props) => {
  const { shop_id = '' } = useParams();
  const stores = authService.slice.selectors.stores();
  const storeInfo = stores.find((f) => f.id === shop_id);
  const printerClassname = clsx(printer && 'printer');
  return (
    <ReceiptContainer ref={billRef} className={printerClassname}>
      <ReceiptWrapper className={printerClassname}>
        <StaffNameTag
          className={printerClassname}
          color_text_1
          title_h7_20_semibold_caplock
          text_center
        >
          {ticketDetail?.staffName || ''}
        </StaffNameTag>
        {/* Header */}
        <Header>
          <Box display='flex' flexDirection='column' gap={'4px'}>
            {printer ? (
              <img
                src={fozito_logo_printer}
                alt='fozito_logo'
                style={{ height: 24, width: 'auto' }}
              />
            ) : (
              <img src={fozito_logo} alt='fozito_logo' />
            )}
            <ReceiptNumber
              className={printerClassname}
              color_text_1
              title_h1_36_semibold
              text_center
              style={{ fontSize: 24 }}
            >
              #{ticketDetail?.ticketNumber}
            </ReceiptNumber>
          </Box>
          <StoreInfo className={printerClassname}>
            <Text
              title_h5_24_semibold
              color_text_5
              className='text-business-name'
            >
              {storeInfo?.businessName}
            </Text>
            <Text content_content_2_16_medium color_text_5>
              {storeInfo?.address}
            </Text>
            <Text content_content_2_16_medium color_text_5>
              {storeInfo?.city && storeInfo?.city + ', '}
              {storeInfo?.state && storeInfo?.state + ', '}
              {storeInfo?.zip}
            </Text>
            <Text content_content_2_16_medium color_text_5>
              {formatPhone(storeInfo?.phone)}
            </Text>
          </StoreInfo>
        </Header>

        {/* Customer Information */}
        <CustomerInfo>
          <CustomerHeader>
            <Text title_h7_20_semibold_caplock color_text_5>
              {ticketDetail?.customerName || 'CUSTOMER: '}
            </Text>
            <Text content_content_2_16_medium color_text_5>
              {ticketDetail?.customerPoint || 0} Points
            </Text>
          </CustomerHeader>
          <Text content_content_2_16_medium color_text_5>
            {formatPhone(ticketDetail?.customerPhone)}
          </Text>
          <Text content_content_2_16_medium color_text_5>
            {ticketDetail ? formatDate(ticketDetail.createdDated) : ''}
          </Text>
        </CustomerInfo>

        {/* Items */}
        <ItemList>
          {ticketDetail?.items?.map((item, index) => (
            <ItemRow key={index}>
              <ItemLabel>
                <Text title_h8_18_medium color_text_5>
                  {item.itemName}
                </Text>
              </ItemLabel>

              <ItemPrice>
                <Box>
                  <Text title_h8_18_medium color_text_5>
                    x{item.quantity}
                  </Text>
                </Box>
                <Box
                  display='flex'
                  alignItems='center'
                  justifyContent='flex-end'
                  width={'100px'}
                >
                  <Text title_h9_18_semibold color_text_5>
                    {formatCurrency(item.price * item.quantity)}
                  </Text>
                </Box>
              </ItemPrice>
            </ItemRow>
          ))}
        </ItemList>
        <Divider />
        <FeeRow>
          <Text title_h8_18_medium color_text_5>
            Subtotal
          </Text>
          <Text title_h8_18_medium_caplock color_text_5>
            {formatCurrency(ticketDetail?.subTotal)}
          </Text>
        </FeeRow>
        <FeeRow>
          <Text title_h8_18_medium color_text_5>
            Sale Tax
          </Text>
          <Text title_h8_18_medium_caplock color_text_5>
            {formatCurrency(ticketDetail?.saleTax)}
          </Text>
        </FeeRow>
        <FeeRow>
          <Text title_h8_18_medium color_text_5>
            Use Tax
          </Text>
          <Text title_h8_18_medium_caplock color_text_5>
            {formatCurrency(ticketDetail?.useTax)}
          </Text>
        </FeeRow>
        <FeeRow>
          <Text title_h8_18_medium color_text_5>
            Disc. By Ticket
          </Text>
          <Text title_h8_18_medium_caplock color_text_5>
            {formatCurrency(ticketDetail?.discount)}
          </Text>
        </FeeRow>
        <FeeRow>
          <Text title_h8_18_medium color_text_5>
            Disc. By Item
          </Text>
          <Text title_h8_18_medium_caplock color_text_5>
            {formatCurrency(ticketDetail?.discountItem)}
          </Text>
        </FeeRow>
        {
          ticketDetail?.payments.find(payment => payment.paymentType === 'CREDIT_CARD') && <FeeRow>
            <Text title_h8_18_medium color_text_5>
              Card Fee
            </Text>
            <Text title_h8_18_medium color_text_5>
              {formatCurrency(ticketDetail?.cardFee)}
            </Text>
          </FeeRow>
        }

        {/* Total */}
        <TotalRow>
          <Text title_h7_20_semibold_caplock color_text_5>
            TOTAL
          </Text>
          <Text title_h7_20_semibold_caplock color_text_5>
            {formatCurrency(ticketDetail?.total)}
          </Text>
        </TotalRow>

        {/* Payment Information */}
        <PaymentSection>
          {ticketDetail?.payments?.map((payment: TicketPayment, index) => (
            <React.Fragment key={index}>
              <PaymentRow style={{ alignItems: 'center' }}>
                <CustomTag title_h9_18_semibold_caplock color_text_1>
                  {getPaymentMethodDisplay(payment.paymentType)}
                </CustomTag>
                <Text title_h9_18_semibold color_text_5>
                  {formatCurrency(payment.amount)}
                </Text>
              </PaymentRow>
              {payment.paymentType === 'CREDIT_CARD' && (
                <>
                  {/* Signature */}
                  <SignatureSection>
                    {payment.signature ? (
                      <>
                        <SignatureContainer>
                          <SignatureImage
                            src={payment.signature}
                            alt='Signature'
                          />
                        </SignatureContainer>
                        <SignatureText
                          caption_caption_3_12_medium_18
                          style={{ margin: '10px 0px' }}
                        >
                          Signature
                        </SignatureText>
                      </>) :
                      <>
                        <SignatureContainer>
                          <Box height={120} />
                        </SignatureContainer>
                        <SignatureText
                          caption_caption_3_12_medium_18
                          style={{ margin: '10px 0px' }}
                        >
                          Signature
                        </SignatureText>
                      </>
                    }
                  </SignatureSection>
                  <Text content_content_3_16_semibold color_text_5>
                    Credit Card:{' '}
                    <CustomTag color_text_1> {payment.last4} </CustomTag>
                  </Text>
                  <Text content_content_3_16_semibold color_text_5>
                    Card Type: {payment.cardType}
                  </Text>
                  <Text content_content_3_16_semibold color_text_5>
                    AuthCode: {payment.authCode}
                  </Text>
                  <Text content_content_3_16_semibold color_text_5>
                    Trans Number #:{' '}
                    <CustomTag color_text_1>
                      {payment.transactionNumber}
                    </CustomTag>
                  </Text>
                  <Text content_content_3_16_semibold color_text_5>
                    Entry Type: {payment?.entryType}
                  </Text>
                </>
              )}
            </React.Fragment>
          ))}
        </PaymentSection>

        <Divider />
        <Footer>
          <Text color_text_5 body_body_1_14_semibold text_center>
            Service charge of 3% will be applied <br />
            when using the Credit Card of Payment
          </Text>
          <Text color_text_5 body_body_1_14_semibold text_center>
            THANK YOU!
          </Text>
          <PosInfo color_text_1 title_h6_20_medium>
            POS STATION #{storage.station_number.get()}
          </PosInfo>
          <Text color_text_5 body_body_1_14_semibold text_center>
            {owner ? 'MERCHANT' : 'CUSTOMER'} COPY
          </Text>
        </Footer>
        <Box marginTop={32} />
        <div style={{ zoom: 0.6 }}>
          {!owner && coupons.length && <CouponDiscount data={coupons} />}
        </div>
      </ReceiptWrapper>
    </ReceiptContainer>
  );
};

export default ReceiptTicketUI;
